import React from "react";
import {DEFAULT_LANG, Lang} from "@i18n/language";
import FlagIcon from "@components/FlagIcon/FlagIcon";
import LanguagePickerModalContent from "@components/LanguagePicker/LangaugePickerModalContent";
import {useModalStore} from "@store/ModalStore";
import {usePublicTranslations} from "@i18n/i18nUtils";

export type LanguageCountryItem = {
    id: string;
    countryName: string;
    languageName: string;
    languageCode: Lang;
    flagCode?: string;
};

export const LANGUAGE_COUNTRY_ITEMS: LanguageCountryItem[] = [
    {
        id: "it",
        countryName: "Italy",
        languageName: "Italiano",
        languageCode: "it",
        flagCode: "it",
    },
    {
        id: "en",
        countryName: "UK",
        languageName: "English",
        languageCode: "en",
        flagCode: "gb",
    },
    {
        id: "fr",
        countryName: "France",
        languageName: "Français",
        languageCode: "fr",
        flagCode: "fr",
    },
    {
        id: "de",
        countryName: "Germany",
        languageName: "Deutsch",
        languageCode: "de",
        flagCode: "de",
    },
    {
        id: "es",
        countryName: "Spain",
        languageName: "Español",
        languageCode: "es",
        flagCode: "es",
    },
    {
        id: "ro",
        countryName: "Romania",
        languageName: "Română",
        languageCode: "ro",
        flagCode: "ro",
    },
    {
        id: "sq",
        countryName: "Albania",
        languageName: "Shqip",
        languageCode: "sq",
        flagCode: "al",
    }
];

export function findLanguageCountryItem(lang?: string) {
    const normalized = lang?.slice(0, 2);

    return (
        LANGUAGE_COUNTRY_ITEMS.find((item) => item.languageCode === normalized) ??
        LANGUAGE_COUNTRY_ITEMS.find((item) => item.languageCode === DEFAULT_LANG) ??
        LANGUAGE_COUNTRY_ITEMS[0]
    );
}

export function LanguagePicker(props: { className?: string }) {
    const {open} = useModalStore();

    const {t, i18n: i18nInstance} = usePublicTranslations();

    const current = findLanguageCountryItem(i18nInstance.language);

    async function openPicker() {
        await open({
            key: "language-picker",
            title: t("public.language.modal.title", {defaultValue: "Choose language"}),
            content: <LanguagePickerModalContent/>,
            showCloseButton: true,
            isDismissable: true,
            className: "appModal--languagePicker",
        });
    }

    return (
        <div className={["languagePickerTrigger", props.className ?? ""].join(" ").trim()}>
            <button
                type="button"
                className="languagePickerTrigger__button"
                onClick={openPicker}
                aria-label={t("public.language.open_picker", {defaultValue: "Open language picker"})}
            >
                <span className="languagePickerTrigger__top">
                    <span className="languagePickerTrigger__label tp-2xs semibold color-gray">
                        {t("public.language.current_prefix", {defaultValue: "Sei in"})}
                    </span>

                    <span className="languagePickerTrigger__current">
                        <FlagIcon
                            code={current.flagCode}
                            alt={current.countryName}
                            className="languagePickerTrigger__flag"
                        />
                        <span className="languagePickerTrigger__country tp-2xs bold">{current.countryName}</span>
                    </span>
                </span>

                <span className="languagePickerTrigger__cta tp-2xs semibold">
                    {t("public.language.change_country", {defaultValue: "Change country"})}
                </span>
            </button>
        </div>
    );
}